/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { coalesce, isNotNil, parseUrl, setField, toString } from '@gh/core-util';
import { distinctUntilChanged$, filter$, map$ } from '@gh/rx/operators';
import { flatMap, isEmpty, omit, tail } from 'lodash';
import { Subscription } from 'rxjs';
import { NavigationActivationEvent, NavigationNode } from './navigation.interfaces';

function getSnapshotUrl(snapshot: ActivatedRouteSnapshot): string {
  return flatMap(tail(snapshot.pathFromRoot), (snapshotInPath) => snapshotInPath.url.map(toString)).join('/');
}

/**
 * Finds snapshot in snapshot tree which belongs to the given <code>route</code>
 *
 * @param topSnapshot
 * @param route
 */
function findSnapshotForRoute(topSnapshot: ActivatedRouteSnapshot, route: string): Maybe<ActivatedRouteSnapshot> {
  const stack: ActivatedRouteSnapshot[] = [topSnapshot];

  while (stack.length) {
    const snapshot = stack.pop();

    if (snapshot) {
      const url = getSnapshotUrl(snapshot);
      if (url === route) {
        return snapshot;
      } else if (url.indexOf(route) !== 0) {
        snapshot.children.forEach((childSnapshot) => stack.push(childSnapshot));
      }
    }
  }

  return;
}

function isNavigationToRouteAllowed(topSnapshot: ActivatedRouteSnapshot, route: string, reuseRoute: boolean): boolean {
  const snapshot = findSnapshotForRoute(topSnapshot, route);
  // if requested route is active and it is allowed to reuse => we run navigation only if route is not empty
  return snapshot ? reuseRoute && !isEmpty(snapshot.queryParams) : true;
}

@Component({
  selector: 'gh-navigation-item',
  template: require('./navigation-item.component.html'),
  styles: [require('./navigation-item.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationItemComponent implements OnInit, OnDestroy {
  collapsed = true;
  activeRoutes: Hash<boolean> = {};
  url = '';

  @Input() node: NavigationNode;

  @Output() activate = new EventEmitter<NavigationActivationEvent>();

  @HostBinding('class.has-parent')
  get hasParent(): boolean {
    const { parent } = this.node;
    return isNotNil(parent) && parent.path.length > 0;
  }

  private subscription: Subscription;

  constructor(private router: Router,
              private location: Location,
              private changeDetector: ChangeDetectorRef) {

  }

  get active(): boolean {
    return !isEmpty(this.activeRoutes);
  }

  ngOnInit(): void {
    this.subscription = this.router.events.pipe(
      filter$((event) => event instanceof NavigationEnd),
      // track only pathname changes (changes in query params do not affect active navigation item)
      map$((event: NavigationEnd) => parseUrl(event.url).pathname),
      distinctUntilChanged$())
      .subscribe(() => this.updateActive());

    const { route } = this.node.item;
    if (route) {
      this.url = this.location.prepareExternalUrl(route);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const { onClick, route, reuseRoute } = this.node.item;
    if (onClick) {
      onClick();
    } else if (route
      && isNavigationToRouteAllowed(this.router.routerState.snapshot.root, route, coalesce(reuseRoute, true))) {
      // We navigate to the requested route only if it is not active.
      this.router.navigate([route], { queryParams: {} });
    }

    event.preventDefault();
  }

  onToggle(): void {
    if (this.node.children.length) {
      this.collapsed = !this.collapsed;
    }
  }

  onChildActivate(event: NavigationActivationEvent): any {
    this.setChildRouteActive(event.route, event.active);

    if (event.active) {
      this.collapsed = false;
    }
  }

  private updateActive(): void {
    if (!this.router.navigated) {
      return;
    }

    const route = this.node.item.route;
    if (route) {
      const active = this.router.isActive(route, false);

      if (active !== this.activeRoutes[route]) {
        this.setChildRouteActive(route, active);
        this.changeDetector.markForCheck();

        this.activate.emit({ route, active });
      }
    }
  }

  private setChildRouteActive(route: string, active: boolean): void {
    this.activeRoutes = active ? setField(this.activeRoutes, route, true) : omit(this.activeRoutes, route);
  }
}
